import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { useSelector } from "react-redux";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { AUTHORITIES } from "../../constants";
import { getReadableDate } from "../../utils";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import DialogAddBatchEvent from "./DialogAddBatchEvent";
import DialogEditBatchEvent from "./DialogEditBatchEvent";

export default function ManageEvents() {
    const { requestAPI, showToast } = useAppContext();
    const { branches = [] } = useSelector((state) => state.stateTemplateConfig?.global || {});

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [events, setEvents] = useState([]);
    const [dialogAdd, setDialogAdd] = useState({ visible: false });
    const [dialogEdit, setDialogEdit] = useState({ visible: false });

    const loadEvents = useCallback(() => {
        requestAPI({
            requestPath: "batch-events",
            requestMethod: "GET",
            setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setEvents(data);
                    setError();
                } else {
                    setError(data?.error || "Couldn't load events");
                }
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    const branchTitleById = useMemo(() => {
        const map = new Map(branches.map((branch) => [branch.id, branch.title]));
        return map;
    }, [branches]);

    const closeAdd = useCallback(() => setDialogAdd((prev) => ({ ...prev, visible: false })), []);
    const closeEdit = useCallback(() => setDialogEdit((prev) => ({ ...prev, visible: false })), []);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Events" />
            <TabHeader
                className="px-3 pt-3"
                title="Batch Events"
                highlights={[`Total - ${events.length} Events`]}
                actionItems={[
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.CREATE_BATCH_EVENT}>
                        <Button
                            icon="pi pi-plus"
                            severity="warning"
                            onClick={() =>
                                setDialogAdd({
                                    visible: true,
                                    closeDialog: closeAdd,
                                    setEvents,
                                    branches,
                                })
                            }
                        />
                    </HasRequiredAuthority>,
                ]}
            />

            <div className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-column gap-2">
                {loading ? (
                    <Loading message="Loading events" />
                ) : error ? (
                    <NoContent error={error} />
                ) : events.length ? (
                    events.map((event) => (
                        <div key={event.id} className="border-1 border-gray-300 border-round p-3 flex flex-column gap-2">
                            <div className="flex align-items-center justify-content-between gap-2">
                                <span className={`${TEXT_NORMAL} font-semibold`}>{event.title}</span>
                                <div className="flex gap-2 align-items-center">
                                    {event.cancelled && <Tag value="Cancelled" severity="danger" pt={{ value: { className: TEXT_SMALL } }} />}
                                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_BATCH_EVENT}>
                                        <Button
                                            icon="pi pi-pencil"
                                            rounded
                                            text
                                            severity="warning"
                                            onClick={() =>
                                                setDialogEdit({
                                                    visible: true,
                                                    closeDialog: closeEdit,
                                                    setEvents,
                                                    branches,
                                                    ...event,
                                                })
                                            }
                                        />
                                    </HasRequiredAuthority>
                                </div>
                            </div>
                            <span className={TEXT_SMALL}>
                                {getReadableDate({ date: event.start_at })} - {getReadableDate({ date: event.end_at })}
                            </span>
                            <span className={`${TEXT_SMALL} text-color-secondary`}>
                                Branch: {branchTitleById.get(event.branch_id) || event.branch_id || "—"} · Batches:{" "}
                                {(event.batch_ids || []).join(", ") || "—"}
                            </span>
                            {event.description && <span className={TEXT_SMALL}>{event.description}</span>}
                        </div>
                    ))
                ) : (
                    <NoContent error="No batch events yet" />
                )}
            </div>

            {dialogAdd.visible && <DialogAddBatchEvent {...dialogAdd} showToast={showToast} requestAPI={requestAPI} />}
            {dialogEdit.visible && <DialogEditBatchEvent {...dialogEdit} showToast={showToast} requestAPI={requestAPI} />}
        </div>
    );
}
